import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { DocumentSectionsResponse, SectionByDocumentIdApiResponse } from 'services/documents/DocumentRepository';
import { DEFAULT_DECIMAL_PLACES, setOfRowsForPricingTable } from '../../components/editor/GridDndEditor/Block/Table/defaultData.tsx';
import { getTextRowsWithCalculatedSubtotals } from '../../components/editor/GridDndEditor/Block/Table/tableUtils/calculateSubTotalsPerRow.ts';
import { updateTotalRowsWithSubtotalCalculation } from '../../components/editor/GridDndEditor/Block/Table/tableUtils/updateTotalRowsWithSubtotalCalculation.ts';
import type { TableColumnType } from '../../components/editor/grid/reduxStore/table.types.ts';
import { TableTypeIdentifier } from '../../components/editor/grid/reduxStore/table.types.ts';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useGetPreviewGridByContentId } from './useGetPreviewGridByContentId.tsx';

interface SectionsContextProps {
  sections: DocumentSectionsResponse;
  sectionsQueryStatus: string;
  updateSections: (newSections: DocumentSectionsResponse) => void;
  activeSectionId?: string;
  setActiveSectionId: (newSectionId: string) => void;
}

const ContentSectionsContext = createContext<SectionsContextProps | undefined>(undefined);

export const ContentSectionsProvider: React.FC<{ children: ReactNode; contentId: string }> = ({ children, contentId }) => {
  const { data: sectionData, status: sectionsQueryStatus } = useGetPreviewGridByContentId(contentId);
  const [sections, setSections] = useState<DocumentSectionsResponse>({} as DocumentSectionsResponse);
  const [activeSectionId, setActiveSectionId] = useState<string>();

  const updateSection = (section: SectionByDocumentIdApiResponse) => {
    return {
      ...section,
      blocks: section.blocks.map((block) => {
        if (block.type === GridBlockType.TABLE && block.content?.rows) {
          const { sumOfSubtotalColumn } = getTextRowsWithCalculatedSubtotals(
            block.content.rows,
            block.content.columns as TableColumnType[]
          );
          const totalRows = block.content?.totalRows;
          const updatedTotalRows = updateTotalRowsWithSubtotalCalculation(totalRows || setOfRowsForPricingTable, sumOfSubtotalColumn);
          return {
            ...block,
            content: {
              ...block.content,
              totalRows: updatedTotalRows,
              rows: block.content.rows,
              metadata: {
                ...(block.content.metadata || {
                  tableType: TableTypeIdentifier.TEXT_TABLE,
                  pricingDecimalPlaces: DEFAULT_DECIMAL_PLACES,
                }),
                tableTotal: updatedTotalRows[updatedTotalRows.length - 1]?.subtotal,
              },
            },
          };
        }
        return block;
      }),
    };
  };

  useEffect(() => {
    if (sectionData && sectionsQueryStatus === 'success') {
      updateSections(sectionData);
    }
  }, [sectionData, sectionsQueryStatus]);

  const updateSections = (newSections: DocumentSectionsResponse) => {
    const calculatedSections = newSections?.sections?.map((section) => updateSection(section)) || [];
    setSections({ sections: calculatedSections });
  };

  return (
    <ContentSectionsContext.Provider value={{ sections, sectionsQueryStatus, updateSections, activeSectionId, setActiveSectionId }}>
      {children}
    </ContentSectionsContext.Provider>
  );
};

export const useContentSectionsForPreview = (): SectionsContextProps => {
  const context = useContext(ContentSectionsContext);
  if (!context) {
    throw new Error('useSections must be used within a SectionsProvider');
  }
  return context;
};
